import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getCourseById, createDirectPayPaymentLink, verifyDirectPayPayment, getDirectPayCards, payWithDirectPayCard } from '../../../../../Constants/Api/functions/function';
import { Button, Spin, Tabs, Card, Form, Input, Select, Space, Divider, DatePicker, TimePicker, Upload, message, Modal, Radio } from 'antd';
import {
    LeftCircleOutlined,
    CreditCardOutlined,
    BankOutlined,
    LockOutlined,
    UserOutlined,
    CalendarOutlined,
    UploadOutlined,
    FileImageOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
import history from '../../../../../../history';

import './styles.scss'
import 'react-quill/dist/quill.bubble.css';

// Card Payment Tab Component
const CardPaymentTab = ({ course, userProfile }) => {
    const [form] = Form.useForm();
    const [isProcessing, setIsProcessing] = useState(false);
    const [savedCards, setSavedCards] = useState([]);
    const [loadingCards, setLoadingCards] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
    const [paymentUrl, setPaymentUrl] = useState('');
    const [selectedInstallment, setSelectedInstallment] = useState(null);

    useEffect(() => {
        // Load saved cards if wallet ID is available
        if (userProfile?.id) {
            loadSavedCards();
        }
    }, [userProfile]);

    // Initialize selected installment (if any) when course data loads
    useEffect(() => {
        if (course?.installmentBreakdown && course.installmentBreakdown.length > 0) {
            setSelectedInstallment(course.installmentBreakdown[0]);
        } else {
            setSelectedInstallment(null);
        }
    }, [course]);

    const loadSavedCards = async () => {
        setLoadingCards(true);
        try {
            // Use user ID as wallet ID (you may need to adjust this based on your DirectPay setup)
            const result = await getDirectPayCards(userProfile.id);
            if (result.success) {
                setSavedCards(result.cards || []);
            }
        } catch (error) {
            console.error('Error loading cards:', error);
        }
        setLoadingCards(false);
    };

    const handleDirectPayPayment = async () => {
        setIsProcessing(true);
        try {
            // Determine amount: either selected installment or full course price
            const amountToPay = selectedInstallment?.amount ?? course?.price;

            const totalInstallments = course?.installments || 0;
            const installmentNumber = selectedInstallment
                ? selectedInstallment.installmentNumber
                : (totalInstallments > 0 ? totalInstallments : 0);

            const paymentData = {
                userId: userProfile?.id,
                courseId: course?.id,
                amount: amountToPay,
                description: selectedInstallment
                    ? `Payment for ${course?.name} - Installment ${selectedInstallment.installmentNumber}`
                    : `Payment for ${course?.name}`,
                customerEmail: userProfile?.email,
                customerMobile: userProfile?.contactNumber || '',
                customerName: userProfile?.name,
                installmentNumber,
                totalInstallments
            };

            const result = await createDirectPayPaymentLink(paymentData);

            if (result.success && result.paymentLink) {
                // Open payment link inside app using a modal webview (iframe)
                setPaymentUrl(result.paymentLink);
                setIsPaymentModalVisible(true);

                // Start polling for payment status
                pollPaymentStatus(result.orderId, result.transactionId);
                
                message.success('Payment page opened. Please complete the payment in the popup.');
            } else {
                message.error(result.message || 'Failed to create payment link');
            }
        } catch (error) {
            console.error('Payment error:', error);
            message.error('An error occurred while processing payment');
        }
        setIsProcessing(false);
    };

    const handleClosePaymentModal = () => {
        setIsPaymentModalVisible(false);
        setPaymentUrl('');
    };

    const pollPaymentStatus = async (orderId, transactionId) => {
        // Poll every 3 seconds for payment status
        const interval = setInterval(async () => {
            try {
                const result = await verifyDirectPayPayment(transactionId, orderId);
                if (result.success && result.status === 'SUCCESS') {
                    clearInterval(interval);
                    message.success('Payment completed successfully!');
                    // Reload and navigate to My Courses page
                    setTimeout(() => {
                        window.location.href = '/dashboard/mycources';
                    }, 1500);
                }
            } catch (error) {
                console.error('Error verifying payment:', error);
            }
        }, 3000);

        // Stop polling after 5 minutes
        setTimeout(() => {
            clearInterval(interval);
        }, 300000);
    };

    const handlePayWithSavedCard = async (cardId) => {
        setIsProcessing(true);
        try {
            // Determine amount: either selected installment or full course price
            const amountToPay = selectedInstallment?.amount ?? course?.price;

            const totalInstallments = course?.installments || 0;
            const installmentNumber = selectedInstallment
                ? selectedInstallment.installmentNumber
                : (totalInstallments > 0 ? totalInstallments : 0);

            const paymentData = {
                userId: userProfile?.id,
                courseId: course?.id,
                walletId: userProfile?.id, // Use user ID as wallet ID
                cardId: cardId,
                amount: amountToPay,
                description: selectedInstallment
                    ? `Payment for ${course?.name} - Installment ${selectedInstallment.installmentNumber}`
                    : `Payment for ${course?.name}`,
                installmentNumber,
                totalInstallments
            };

            const result = await payWithDirectPayCard(paymentData);

            if (result.success) {
                message.success('Payment completed successfully!');
                setTimeout(() => {
                    window.location.href = '/dashboard/mycources';
                }, 1500);
            } else {
                message.error(result.message || 'Payment failed');
            }
        } catch (error) {
            console.error('Card payment error:', error);
            message.error('An error occurred while processing payment');
        }
        setIsProcessing(false);
    };

    // Amount to display on buttons based on selected option
    const displayAmount = selectedInstallment?.amount ?? course?.price;

    return (
        <div className="payment-tab card-payment">
            <div className="payment-summary">
                <h3>Payment Summary</h3>
                <div className="summary-item">
                    <span>Course:</span>
                    <span>{course?.name}</span>
                </div>
                <div className="summary-item">
                    <span>Total Course Fee:</span>
                    <span className="amount">{course?.price} LKR</span>
                </div>

                {/* Installment options */}
                {course?.installmentBreakdown && course.installmentBreakdown.length > 0 && (
                    <>
                        <Divider />
                        <div className="summary-item">
                            <span>Choose Payment Option:</span>
                        </div>
                        <Radio.Group
                            onChange={(e) => setSelectedInstallment(e.target.value)}
                            value={selectedInstallment}
                            style={{ width: '100%' }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                {course.installmentBreakdown.map((inst) => (
                                    <Radio key={inst.installmentNumber} value={inst}>
                                        Installment {inst.installmentNumber}: {inst.amount} LKR
                                    </Radio>
                                ))}
                                <Radio value={null}>
                                    Pay full amount: {course?.price} LKR
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </>
                )}

                <Divider />
            </div>

            {/* Saved Cards Section */}
            {savedCards.length > 0 && (
                <div className="saved-cards-section" style={{ marginBottom: 24 }}>
                    <h4><CreditCardOutlined /> Saved Cards</h4>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {savedCards.map((card) => (
                            <Card
                                key={card.card_id}
                                size="small"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setSelectedCard(card.card_id)}
                                className={selectedCard === card.card_id ? 'selected-card' : ''}
                            >
                                <Space>
                                    <CreditCardOutlined />
                                    <span>**** **** **** {card.last_4_digits || card.masked_card || '****'}</span>
                                    <span style={{ marginLeft: 'auto' }}>
                                        {card.card_brand || 'Card'}
                                    </span>
                                </Space>
                            </Card>
                        ))}
                        {selectedCard && (
                            <Button
                                type="primary"
                                block
                                onClick={() => handlePayWithSavedCard(selectedCard)}
                                loading={isProcessing}
                                style={{ marginTop: 8 }}
                            >
                                Pay with Selected Card
                            </Button>
                        )}
                    </Space>
                    <Divider>OR</Divider>
                </div>
            )}

            {/* DirectPay Payment Button */}
            <div className="directpay-payment-section">
                <h4><CreditCardOutlined /> Pay with DirectPay</h4>
                <p style={{ marginBottom: 16, color: '#666' }}>
                    Click the button below to complete your payment securely through DirectPay.
                </p>
                
                <Button
                    type="primary"
                    size="large"
                    loading={isProcessing}
                    block
                    onClick={handleDirectPayPayment}
                    className="pay-button"
                    icon={<CreditCardOutlined />}
                >
                    {isProcessing
                        ? 'Processing Payment...'
                        : `Pay ${displayAmount} LKR with DirectPay`}
                </Button>

                <div className="security-notice" style={{ marginTop: 16, textAlign: 'center' }}>
                    <LockOutlined /> Your payment information is secure and encrypted
                </div>
            </div>

            {/* DirectPay payment popup */}
            <Modal
                open={isPaymentModalVisible}
                onCancel={handleClosePaymentModal}
                footer={null}
                width={900}
                destroyOnClose
                centered
                title="Complete Your Payment"
                bodyStyle={{ padding: 0, height: '70vh' }}
            >
                {paymentUrl && (
                    <iframe
                        src={paymentUrl}
                        title="DirectPay Payment"
                        style={{ border: 'none', width: '100%', height: '100%' }}
                    />
                )}
            </Modal>
        </div>
    );
};

// Bank Deposit Tab Component
const BankDepositTab = ({ course, userProfile }) => {
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);

    const handleSubmitPaymentDetails = async (values) => {
        setIsSubmitting(true);
        try {
            // Here you would submit the payment details to your backend
            console.log('Submitting payment details:', values);
            console.log('Uploaded file:', uploadedFile);

            // Simulate API call
            setTimeout(() => {
                message.success('Payment details submitted successfully! We will verify your payment and enroll you in the course.');
                setIsSubmitting(false);
                form.resetFields();
                setUploadedFile(null);
            }, 2000);
        } catch (error) {
            console.error('Error submitting payment details:', error);
            message.error('Failed to submit payment details. Please try again.');
            setIsSubmitting(false);
        }
    };

    const uploadProps = {
        name: 'receipt',
        listType: 'picture-card',
        className: 'receipt-uploader',
        maxCount: 1,
        accept: '.jpg,.jpeg,.png,.pdf',
        beforeUpload: (file) => {
            const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type);
            const isValidSize = file.size / 1024 / 1024 < 5; // 5MB

            if (!isValidType) {
                message.error('You can only upload JPG/PNG/PDF files!');
                return false;
            }
            if (!isValidSize) {
                message.error('File must be smaller than 5MB!');
                return false;
            }

            setUploadedFile(file);
            return false; // Prevent auto upload
        },
        onRemove: () => {
            setUploadedFile(null);
        },
    };

    return (
        <div className="payment-tab bank-deposit">
            <div className="payment-summary">
                <h3>Payment Summary</h3>
                <div className="summary-item">
                    <span>Course:</span>
                    <span>{course?.name}</span>
                </div>
                <div className="summary-item">
                    <span>Amount:</span>
                    <span className="amount">{course?.price} LKR</span>
                </div>
                <Divider />
            </div>

            <div className="bank-instructions">
                <h4><BankOutlined /> Bank Transfer Instructions</h4>
                <p>Please follow the following steps to complete your payment:</p>

                <div className="bank-details">
                    <h5>Bank Account Details:</h5>
                    <div className="account-info">
                        <div className="info-row">
                            <span className="label">Account Number:</span>
                            <span className="value">208-2-001-3-5001477</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Bank:</span>
                            <span className="value">Peoples Bank</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Branch:</span>
                            <span className="value">Battaramulla</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Account Name:</span>
                            <span className="value">P.K.S.K.Kotuwegedara</span>
                        </div>
                    </div>
                </div>

                <div className="important-notes">
                    <h5>Important Notes:</h5>
                    <ul>
                        <li>Please include your name and course name in the transaction description</li>
                        <li>Keep the transaction receipt safe for verification</li>
                        <li>Processing may take 1-2 business days</li>
                    </ul>
                </div>
            </div>

            
            <div className="alternative-contact">
                <Divider />
                <h5>Alternative: Send Payment Confirmation</h5>
                <p>If you prefer to send the details manually, you can also contact us through:</p>

                <Space direction="vertical" style={{ width: '100%' }}>
                    <div className="contact-item">
                        <span>Email: </span>
                        <a href="mailto:Payment@digitalacademy.lk">Payment@digitalacademy.lk</a>
                        <Button
                            type='link'
                            size="small"
                            style={{ padding: 0, marginLeft: 10 }}
                            href='mailto:Payment@digitalacademy.lk'
                        >
                            Send Email
                        </Button>
                    </div>

                    <div className="contact-item">
                        <span>WhatsApp: </span>
                        <a href={`https://wa.me/+94774747920?text=Hello%2C%20I%20am%20${userProfile?.name}%20(Index%20No:%20${userProfile?.indexNumber}).%20I%20have%20made%20the%20payment%20for%20the%20course%20${course?.name}%20and%20here%20is%20the%20transaction%20receipt.`} target='_blank'>
                            +94 77 474 7920
                        </a>
                        <Button
                            type='link'
                            size="small"
                            style={{ padding: 0, marginLeft: 10 }}
                            href={`https://wa.me/+94774747920?text=Hello%2C%20I%20am%20${userProfile?.name}%20(Index%20No:%20${userProfile?.indexNumber}).%20I%20have%20made%20the%20payment%20for%20the%20course%20${course?.name}%20and%20here%20is%20the%20transaction%20receipt.`}
                            target='_blank'
                        >
                            Send WhatsApp
                        </Button>
                    </div>
                </Space>
            </div>

            <Divider />

            <div className="payment-details-form">
                <h4><FileImageOutlined /> Submit Payment Details</h4>
                <p>After making the bank transfer, please fill in the details below and upload your receipt:</p>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmitPaymentDetails}
                    className="payment-form"
                >
                    <div className="form-grid">
                        <Form.Item
                            label="Course Name"
                            name="courseName"
                            initialValue={course?.name}
                            rules={[{ required: true, message: 'Course name is required' }]}
                        >
                            <Input disabled />
                        </Form.Item>

                        <Form.Item
                            label="Reference/Transaction ID"
                            name="transactionId"
                            rules={[{ required: true, message: 'Transaction ID is required' }]}
                        >
                            <Input placeholder="Enter transaction/reference number" />
                        </Form.Item>

                        <Form.Item
                            label="Date"
                            name="paymentDate"
                            rules={[{ required: true, message: 'Payment date is required' }]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                placeholder="Select payment date"
                                format="YYYY-MM-DD"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Time"
                            name="paymentTime"
                            rules={[{ required: true, message: 'Payment time is required' }]}
                        >
                            <TimePicker
                                style={{ width: '100%' }}
                                placeholder="Select payment time"
                                format="HH:mm"
                                use12Hours={false}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        label="Remark/Description"
                        name="remark"
                        rules={[{ required: true, message: 'Remark is required' }]}
                    >
                        <Input.TextArea
                            placeholder="Enter any additional remarks or description"
                            rows={3}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Upload Receipt"
                        name="receipt"
                        rules={[{ required: true, message: 'Please upload your payment receipt' }]}
                    >
                        <Upload {...uploadProps}>
                            {uploadedFile ? null : (
                                <div className="upload-area">
                                    <UploadOutlined />
                                    <div style={{ marginTop: 8 }}>
                                        Click to upload receipt
                                        <br />
                                    </div>
                                </div>
                            )}
                        </Upload>
                        
                    <small className='support-text'>Support: JPG, PNG, PDF (Max: 5MB)</small>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            loading={isSubmitting}
                            block
                            className="submit-button"
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Payment Details'}
                        </Button>
                    </Form.Item>
                </Form>
            </div>

            <div className="confirmation-notice">
                <p><strong>Note:</strong> After verifying your payment, you will be enrolled in the course and receive a confirmation email.</p>
            </div>
        </div>
    );
};

const BuyCourse = () => {
    const allCources = useSelector(state => state.allCources.allCources);
    const [course, setCourse] = useState()
    const courseId = useParams().id
    const [isLoading, setIsLoading] = useState(false)
    const [activeTab, setActiveTab] = useState('card')
    const userProfile = useSelector(state => state.userProfile.userProfile);

    useEffect(() => {
        localStorage.setItem('digital-academy-last-visit', courseId);
    }, []);

    useEffect(() => {
        // Always fetch latest course details (including installment breakdown)
        // from backend, but use Redux data as a quick initial value
        if (allCources) {
            const fromStore = allCources.find(item => item.id === courseId);
            if (fromStore) {
                setCourse(fromStore);
            }
        }
        handleGetCourse();
    }, [allCources]);

    const handleGetCourse = async () => {
        setIsLoading(true);
        try {
            const response = await getCourseById(courseId);
            if (response) {
                // New API returns a single course object with installmentBreakdown
                const courseData = Array.isArray(response) ? response[0] : response;
                setCourse(courseData);
            }
        } catch (error) {
            console.error('handleGetCourse', error);
        }
        setIsLoading(false);
    }

    return (
        <div className='buy-course'>
            <Button
                type='link'
                style={{
                    margin: '10px 0 0 0',
                    padding: 0
                }}
                onClick={() => history.goBack()}
            >
                <LeftCircleOutlined /> Go Back
            </Button>
            <h1 className='buy-title' style={{ marginTop: 5 }}>Buy Course</h1>
            <Spin spinning={isLoading}>
                <div className='course-data'>
                    <h2>Course Name: {course?.name}</h2>
                    <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: course?.description }} />
                    <h3>{course?.price} LKR</h3>
                    <p>User Name: {userProfile?.name}</p>
                </div>
            </Spin>

            <div className='payment-section'>
                <h2>Select Payment Method</h2>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    type="card"
                    size="large"
                    items={[
                        {
                            key: 'card',
                            label: (
                                <span>
                                    <CreditCardOutlined />
                                    Card Payment
                                </span>
                            ),
                            children: (
                                <CardPaymentTab course={course} userProfile={userProfile} />
                            ),
                        },
                        {
                            key: 'bank',
                            label: (
                                <span>
                                    <BankOutlined />
                                    Bank Deposit
                                </span>
                            ),
                            children: (
                                <BankDepositTab course={course} userProfile={userProfile} />
                            ),
                        },
                    ]}
                />
            </div>
        </div>
    )
}

export default BuyCourse